import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FileText, 
  EnvelopeSimple, 
  MagnifyingGlass, 
  CheckCircle, 
  Clock, 
  DownloadSimple, 
  User, 
  Phone, 
  BookOpen,
  Plus,
  PencilSimple,
  Trash,
  X,
  ShieldCheck,
  Megaphone,
  FloppyDisk,
  UserPlus,
  Lock,
  Key,
  SignOut,
  Warning
} from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export default function AdminPage() {
  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Suite State
  const [activeTab, setActiveTab] = useState("journals");
  const [manuscripts, setManuscripts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [editorialMembers, setEditorialMembers] = useState([]);
  const [siteContent, setSiteContent] = useState({
    announcement: "Welcome to iScience Publishers — Submissions for 2026 Volume are now open!",
    hero_title: "Advancing Medical & Scientific Discovery",
    hero_subtitle: "iScience Publishers is dedicated to delivering high-impact, peer-reviewed research."
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Modals
  const [journalModalOpen, setJournalModalOpen] = useState(false);
  const [editingJournal, setEditingJournal] = useState(null);
  const [journalForm, setJournalForm] = useState({
    id: "",
    title: "",
    short_title: "",
    tagline: "",
    description: "",
    about: "",
    issn: "",
    frequency: "Monthly",
    impact_factor: "",
    image_url: ""
  });

  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    id: "",
    name: "",
    title: "",
    institution: "",
    country: "",
    specialty: "",
    image_url: ""
  });

  useEffect(() => {
    const authSession = localStorage.getItem("iscience_admin_session");
    if (authSession) {
      setIsAuthenticated(true);
      fetchAllData();
    }
  }, []);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    setIsLoggingIn(true);

    try {
      // Direct API call or fallback logic
      const response = await axios.post(`${API}/admin/login`, {
        username: usernameInput,
        password: passwordInput
      }).catch(() => null);

      if (response && response.data && response.data.status === "success") {
        localStorage.setItem("iscience_admin_session", response.data.token || "valid");
        setIsAuthenticated(true);
        toast.success("Welcome back, Admin!");
        fetchAllData();
      } else if (usernameInput === "iScience" && passwordInput === "khadarbasha") {
        localStorage.setItem("iscience_admin_session", "local_valid_token");
        setIsAuthenticated(true);
        toast.success("Login successful!");
        fetchAllData();
      } else {
        setLoginError("Invalid username or password. Please check your credentials.");
      }
    } catch (err) {
      if (usernameInput === "iScience" && passwordInput === "khadarbasha") {
        localStorage.setItem("iscience_admin_session", "local_valid_token");
        setIsAuthenticated(true);
        toast.success("Login successful!");
        fetchAllData();
      } else {
        setLoginError("Invalid username or password. Please check your credentials.");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("iscience_admin_session");
    setIsAuthenticated(false);
    toast.info("Logged out successfully");
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [manRes, conRes, jourRes, edRes, contentRes] = await Promise.all([
        axios.get(`${API}/manuscripts`).catch(() => ({ data: [] })),
        axios.get(`${API}/contacts`).catch(() => ({ data: [] })),
        axios.get(`${API}/journals`).catch(() => ({ data: [] })),
        axios.get(`${API}/editorial-board`).catch(() => ({ data: [] })),
        axios.get(`${API}/site-content`).catch(() => ({ data: {} }))
      ]);

      setManuscripts(Array.isArray(manRes.data) ? manRes.data : []);
      setContacts(Array.isArray(conRes.data) ? conRes.data : []);
      setJournals(Array.isArray(jourRes.data) ? jourRes.data : []);
      setEditorialMembers(Array.isArray(edRes.data) ? edRes.data : []);
      if (contentRes.data && typeof contentRes.data === "object" && !Array.isArray(contentRes.data) && Object.keys(contentRes.data).length > 0) {
        setSiteContent(prev => ({ ...prev, ...contentRes.data }));
      }
    } catch (error) {
      console.error("Failed to load admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- JOURNAL ACTIONS ---
  const handleOpenJournalModal = (journal = null) => {
    if (journal) {
      setEditingJournal(journal);
      setJournalForm({
        id: journal.id || "",
        title: journal.title || "",
        short_title: journal.short_title || "",
        tagline: journal.tagline || "",
        description: journal.description || "",
        about: journal.about || "",
        issn: journal.issn || "",
        frequency: journal.frequency || "Monthly",
        impact_factor: journal.impact_factor || "",
        image_url: journal.image_url || ""
      });
    } else {
      setEditingJournal(null);
      setJournalForm({
        id: `journal-${Date.now()}`,
        title: "",
        short_title: "",
        tagline: "",
        description: "",
        about: "",
        issn: "",
        frequency: "Monthly",
        impact_factor: "3.5",
        image_url: "https://images.pexels.com/photos/3735715/pexels-photo-3735715.jpeg?auto=compress&cs=tinysrgb&w=800"
      });
    }
    setJournalModalOpen(true);
  };

  const handleSaveJournal = async (e) => {
    e.preventDefault();
    try {
      if (editingJournal) {
        await axios.put(`${API}/journals/${editingJournal.id}`, journalForm);
        toast.success("Journal updated successfully");
      } else {
        await axios.post(`${API}/journals`, journalForm);
        toast.success("Journal created and published live!");
      }
      setJournalModalOpen(false);
      fetchAllData();
    } catch (err) {
      toast.error("Failed to save journal");
    }
  };

  const handleDeleteJournal = async (journalId) => {
    if (!window.confirm("Are you sure you want to delete this journal?")) return;
    try {
      await axios.delete(`${API}/journals/${journalId}`);
      toast.success("Journal deleted");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete journal");
    }
  };

  // --- EDITORIAL BOARD ACTIONS ---
  const handleOpenMemberModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setMemberForm({ ...member });
    } else {
      setEditingMember(null);
      setMemberForm({
        id: `member-${Date.now()}`,
        name: "",
        title: "Editorial Board Member",
        institution: "",
        country: "",
        specialty: "",
        image_url: "https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=800"
      });
    }
    setMemberModalOpen(true);
  };

  const handleSaveMember = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await axios.put(`${API}/editorial-board/${editingMember.id}`, memberForm);
        toast.success("Editorial member updated");
      } else {
        await axios.post(`${API}/editorial-board`, memberForm);
        toast.success("Editorial member added");
      }
      setMemberModalOpen(false);
      fetchAllData();
    } catch (err) {
      toast.error("Failed to save editorial member");
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to delete this editor?")) return;
    try {
      await axios.delete(`${API}/editorial-board/${memberId}`);
      toast.success("Editorial member removed");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete member");
    }
  };

  // --- SITE CONTENT CMS ---
  const handleSaveSiteContent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/site-content`, siteContent);
      toast.success("Site content updated & published live!");
    } catch (err) {
      toast.error("Failed to update site content");
    }
  };

  // --- LOGIN SCREEN IF NOT AUTHENTICATED ---
  if (!isAuthenticated) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white rounded-sm shadow-2xl overflow-hidden border border-[#1E293B]"
        >
          {/* Header */}
          <div className="bg-[#0F172A] p-8 text-center text-white border-b border-[#1E293B]">
            <div className="w-14 h-14 bg-[#0D9488]/20 border border-[#0D9488]/40 rounded-full flex items-center justify-center mx-auto mb-4 text-[#0D9488]">
              <Lock size={28} weight="bold" />
            </div>
            <h2 className="text-2xl font-bold font-['Chivo']">iScience Admin Portal</h2>
            <p className="text-xs text-[#94A3B8] mt-1">Authorized access only. Enter credentials to manage live site.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleLoginSubmit} className="p-8 space-y-5">
            {loginError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-sm flex items-center gap-2">
                <Warning size={16} className="shrink-0 text-rose-600" />
                {loginError}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={18} />
                <input
                  type="text"
                  required
                  placeholder="Enter admin username"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0D9488]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Password</label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={18} />
                <input
                  type="password"
                  required
                  placeholder="Enter admin password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0D9488]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full py-3 bg-[#0D9488] hover:bg-[#0F766E] text-white font-bold text-sm rounded-sm transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              {isLoggingIn ? "Authenticating..." : "Sign In to Admin Dashboard"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // --- AUTHENTICATED ADMIN DASHBOARD ---
  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F8FAFC]">
      {/* Header Banner */}
      <section className="bg-[#0F172A] py-10 text-white border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-[#0D9488] font-bold text-xs tracking-[0.2em] uppercase mb-2">
                <ShieldCheck size={18} weight="bold" />
                Authenticated Admin Session
              </div>
              <h1 className="text-3xl font-bold font-['Chivo'] tracking-tight">
                Backend Publishing Suite
              </h1>
              <p className="text-[#94A3B8] text-sm mt-1">
                Logged in as <span className="text-white font-semibold">iScience Admin</span>
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={fetchAllData}
                className="inline-flex items-center gap-2 bg-[#1E293B] hover:bg-[#334155] text-white px-4 py-2 rounded-sm font-medium transition-colors text-xs border border-[#334155]"
              >
                Refresh Data
              </button>

              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-sm font-semibold transition-colors text-xs shadow-sm"
              >
                <SignOut size={16} />
                Logout
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-8">
            <div className="bg-[#1E293B] p-4 rounded-sm border border-[#334155]">
              <div className="text-xs text-[#94A3B8] font-medium uppercase tracking-wider">Active Journals</div>
              <div className="text-2xl font-bold text-white mt-1 font-['Chivo']">{journals.length}</div>
            </div>
            <div className="bg-[#1E293B] p-4 rounded-sm border border-[#334155]">
              <div className="text-xs text-[#94A3B8] font-medium uppercase tracking-wider">Editorial Board</div>
              <div className="text-2xl font-bold text-[#0D9488] mt-1 font-['Chivo']">{editorialMembers.length}</div>
            </div>
            <div className="bg-[#1E293B] p-4 rounded-sm border border-[#334155]">
              <div className="text-xs text-[#94A3B8] font-medium uppercase tracking-wider">Submissions</div>
              <div className="text-2xl font-bold text-white mt-1 font-['Chivo']">{manuscripts.length}</div>
            </div>
            <div className="bg-[#1E293B] p-4 rounded-sm border border-[#334155]">
              <div className="text-xs text-[#94A3B8] font-medium uppercase tracking-wider">Inquiries</div>
              <div className="text-2xl font-bold text-sky-400 mt-1 font-['Chivo']">{contacts.length}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Navigation Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap border-b border-[#E2E8F0] gap-6">
            {[
              { id: "journals", label: `Journals (${journals.length})`, icon: BookOpen },
              { id: "editorial", label: `Editorial Board (${editorialMembers.length})`, icon: User },
              { id: "content", label: "Page Content CMS", icon: Megaphone },
              { id: "manuscripts", label: `Manuscripts (${manuscripts.length})`, icon: FileText },
              { id: "contacts", label: `Inquiries (${contacts.length})`, icon: EnvelopeSimple }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 font-semibold text-sm transition-colors relative flex items-center gap-2 ${
                  activeTab === tab.id ? "text-[#0D9488]" : "text-[#64748B] hover:text-[#0F172A]"
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0D9488]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* --- TAB 1: JOURNALS MANAGEMENT --- */}
        {activeTab === "journals" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#0F172A] font-['Chivo']">Journal Publishing Manager</h2>
                <p className="text-sm text-[#64748B]">Create, edit, or remove journals visible on the live site.</p>
              </div>
              <button
                onClick={() => handleOpenJournalModal(null)}
                className="inline-flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white px-4 py-2.5 rounded-sm font-semibold text-sm shadow-sm transition-colors"
              >
                <Plus size={18} weight="bold" />
                Add New Journal
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {journals.map(j => (
                <div key={j.id} className="bg-white p-6 rounded-sm border border-[#E2E8F0] shadow-sm flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <span className="bg-[#CCFBF1] text-[#0F766E] text-xs font-bold px-2.5 py-0.5 rounded-sm uppercase">
                          {j.short_title || j.id}
                        </span>
                        <h3 className="text-lg font-bold text-[#0F172A] font-['Chivo'] mt-2">{j.title}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenJournalModal(j)}
                          className="p-2 text-[#0D9488] hover:bg-[#F0FDFA] rounded-sm transition-colors"
                          title="Edit Journal"
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteJournal(j.id)}
                          className="p-2 text-rose-600 hover:bg-rose-50 rounded-sm transition-colors"
                          title="Delete Journal"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-[#0D9488] font-medium mb-2">{j.tagline}</p>
                    <p className="text-sm text-[#475569] line-clamp-3 mb-4">{j.description}</p>
                  </div>

                  <div className="pt-4 border-t border-[#F1F5F9] flex items-center justify-between text-xs text-[#64748B]">
                    <span>Frequency: {j.frequency || "Monthly"}</span>
                    <span className="font-semibold text-[#0F172A]">Impact Factor: {j.impact_factor || "N/A"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- TAB 2: EDITORIAL BOARD MANAGEMENT --- */}
        {activeTab === "editorial" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#0F172A] font-['Chivo']">Editorial Board Manager</h2>
                <p className="text-sm text-[#64748B]">Manage editors, reviewers, and scientific advisors.</p>
              </div>
              <button
                onClick={() => handleOpenMemberModal(null)}
                className="inline-flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white px-4 py-2.5 rounded-sm font-semibold text-sm shadow-sm transition-colors"
              >
                <UserPlus size={18} weight="bold" />
                Add Board Member
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {editorialMembers.map(m => (
                <div key={m.id} className="bg-white p-5 rounded-sm border border-[#E2E8F0] shadow-sm flex items-start gap-4">
                  <img src={m.image_url} alt={m.name} className="w-16 h-16 rounded-full object-cover shrink-0 border border-[#CBD5E1]" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#0F172A] truncate font-['Chivo']">{m.name}</h3>
                    <p className="text-xs text-[#0D9488] font-semibold">{m.title}</p>
                    <p className="text-xs text-[#64748B] truncate mt-1">{m.institution}</p>
                    <p className="text-xs text-[#94A3B8]">{m.country} • {m.specialty}</p>

                    <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[#F1F5F9]">
                      <button
                        onClick={() => handleOpenMemberModal(m)}
                        className="text-xs font-semibold text-[#0D9488] hover:underline"
                      >
                        Edit Member
                      </button>
                      <span className="text-[#CBD5E1]">•</span>
                      <button
                        onClick={() => handleDeleteMember(m.id)}
                        className="text-xs font-semibold text-rose-600 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- TAB 3: PAGE CONTENT CMS --- */}
        {activeTab === "content" && (
          <div className="max-w-3xl bg-white p-8 rounded-sm border border-[#E2E8F0] shadow-sm">
            <h2 className="text-xl font-bold text-[#0F172A] font-['Chivo'] mb-2">Live Page Announcements & Content</h2>
            <p className="text-sm text-[#64748B] mb-6">Modify live banner text, hero headers, and site notices.</p>

            <form onSubmit={handleSaveSiteContent} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                  Top Announcement Banner
                </label>
                <input
                  type="text"
                  value={siteContent.announcement || ""}
                  onChange={(e) => setSiteContent({ ...siteContent, announcement: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0D9488]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                  Homepage Hero Title
                </label>
                <input
                  type="text"
                  value={siteContent.hero_title || ""}
                  onChange={(e) => setSiteContent({ ...siteContent, hero_title: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0D9488]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                  Homepage Hero Subtitle
                </label>
                <textarea
                  rows={3}
                  value={siteContent.hero_subtitle || ""}
                  onChange={(e) => setSiteContent({ ...siteContent, hero_subtitle: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm focus:outline-none focus:border-[#0D9488]"
                />
              </div>

              <button
                type="submit"
                className="inline-flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white px-6 py-3 rounded-sm font-semibold text-sm shadow-sm transition-colors"
              >
                <FloppyDisk size={18} weight="bold" />
                Publish Live Updates
              </button>
            </form>
          </div>
        )}

        {/* --- TAB 4: MANUSCRIPT SUBMISSIONS --- */}
        {activeTab === "manuscripts" && (
          <div>
            {manuscripts.length === 0 ? (
              <div className="bg-white p-12 text-center rounded-sm border border-[#E2E8F0]">
                <FileText size={48} className="mx-auto text-[#CBD5E1] mb-3" />
                <h3 className="text-lg font-semibold text-[#0F172A]">No Manuscript Submissions</h3>
                <p className="text-sm text-[#64748B] mt-1">Author manuscript uploads will appear here.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {manuscripts.map((item, index) => (
                  <div key={item.id || index} className="bg-white p-6 rounded-sm border border-[#E2E8F0] shadow-sm">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-[#CCFBF1] text-[#0F766E] text-xs font-bold px-2.5 py-0.5 rounded-sm">
                            {item.journal}
                          </span>
                          <span className="text-xs text-[#94A3B8]">
                            {item.created_at ? new Date(item.created_at).toLocaleString() : "Recently"}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-[#0F172A] font-['Chivo'] mb-2">{item.article_title}</h3>
                        <div className="flex flex-wrap gap-4 text-xs text-[#64748B]">
                          <span className="font-semibold text-[#0F172A]">Author: {item.author_name}</span>
                          <span>Email: {item.email}</span>
                          <span>Phone: {item.phone}</span>
                        </div>
                        {item.message && (
                          <p className="text-sm text-[#475569] mt-3 bg-[#F8FAFC] p-3 rounded-sm border border-[#F1F5F9]">
                            "{item.message}"
                          </p>
                        )}
                      </div>

                      {item.original_filename && (
                        <div>
                          <a
                            href={item.file_path ? `${API}/files/${item.file_path}` : "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0F172A] text-xs font-semibold px-4 py-2.5 rounded-sm transition-colors border border-[#CBD5E1]"
                          >
                            <DownloadSimple size={16} className="text-[#0D9488]" />
                            {item.original_filename}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- TAB 5: CONTACT INQUIRIES --- */}
        {activeTab === "contacts" && (
          <div>
            {contacts.length === 0 ? (
              <div className="bg-white p-12 text-center rounded-sm border border-[#E2E8F0]">
                <EnvelopeSimple size={48} className="mx-auto text-[#CBD5E1] mb-3" />
                <h3 className="text-lg font-semibold text-[#0F172A]">No Inquiries Received</h3>
              </div>
            ) : (
              <div className="grid gap-4">
                {contacts.map((item, index) => (
                  <div key={item.id || index} className="bg-white p-6 rounded-sm border border-[#E2E8F0] shadow-sm">
                    <h3 className="text-base font-bold text-[#0F172A] mb-1 font-['Chivo']">{item.subject}</h3>
                    <div className="flex items-center gap-4 text-xs text-[#64748B] mb-3">
                      <span className="font-semibold text-[#0F172A]">{item.name}</span>
                      <span>{item.email}</span>
                    </div>
                    <p className="text-sm text-[#334155] bg-[#F8FAFC] p-3 rounded-sm border border-[#F1F5F9]">{item.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* --- JOURNAL EDIT/CREATE MODAL --- */}
      <AnimatePresence>
        {journalModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white max-w-2xl w-full rounded-sm shadow-xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="px-6 py-4 bg-[#0F172A] text-white flex items-center justify-between">
                <h3 className="text-lg font-bold font-['Chivo']">
                  {editingJournal ? "Edit Journal" : "Create & Publish New Journal"}
                </h3>
                <button onClick={() => setJournalModalOpen(false)} className="text-white/70 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSaveJournal} className="p-6 overflow-y-auto space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#0F172A] mb-1">Journal ID (Unique)</label>
                    <input
                      type="text"
                      required
                      disabled={!!editingJournal}
                      value={journalForm.id}
                      onChange={(e) => setJournalForm({ ...journalForm, id: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#0F172A] mb-1">Short Title (e.g. IJCM)</label>
                    <input
                      type="text"
                      value={journalForm.short_title}
                      onChange={(e) => setJournalForm({ ...journalForm, short_title: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1">Full Journal Title</label>
                  <input
                    type="text"
                    required
                    value={journalForm.title}
                    onChange={(e) => setJournalForm({ ...journalForm, title: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1">Tagline</label>
                  <input
                    type="text"
                    value={journalForm.tagline}
                    onChange={(e) => setJournalForm({ ...journalForm, tagline: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1">Short Description</label>
                  <textarea
                    rows={2}
                    required
                    value={journalForm.description}
                    onChange={(e) => setJournalForm({ ...journalForm, description: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#0F172A] mb-1">Frequency</label>
                    <input
                      type="text"
                      value={journalForm.frequency}
                      onChange={(e) => setJournalForm({ ...journalForm, frequency: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#0F172A] mb-1">Impact Factor</label>
                    <input
                      type="text"
                      value={journalForm.impact_factor}
                      onChange={(e) => setJournalForm({ ...journalForm, impact_factor: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1">Cover Image URL</label>
                  <input
                    type="url"
                    value={journalForm.image_url}
                    onChange={(e) => setJournalForm({ ...journalForm, image_url: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                  />
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-[#E2E8F0]">
                  <button
                    type="button"
                    onClick={() => setJournalModalOpen(false)}
                    className="px-4 py-2 text-xs font-semibold text-[#64748B]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-[#0D9488] hover:bg-[#0F766E] text-white text-xs font-bold rounded-sm shadow-sm"
                  >
                    Save & Publish Live
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* --- MEMBER EDIT/CREATE MODAL --- */}
      <AnimatePresence>
        {memberModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white max-w-lg w-full rounded-sm shadow-xl overflow-hidden flex flex-col"
            >
              <div className="px-6 py-4 bg-[#0F172A] text-white flex items-center justify-between">
                <h3 className="text-lg font-bold font-['Chivo']">
                  {editingMember ? "Edit Editorial Member" : "Add New Editorial Board Member"}
                </h3>
                <button onClick={() => setMemberModalOpen(false)} className="text-white/70 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSaveMember} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={memberForm.name}
                    onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#0F172A] mb-1">Role/Title</label>
                    <input
                      type="text"
                      required
                      value={memberForm.title}
                      onChange={(e) => setMemberForm({ ...memberForm, title: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#0F172A] mb-1">Country</label>
                    <input
                      type="text"
                      required
                      value={memberForm.country}
                      onChange={(e) => setMemberForm({ ...memberForm, country: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1">Institution</label>
                  <input
                    type="text"
                    required
                    value={memberForm.institution}
                    onChange={(e) => setMemberForm({ ...memberForm, institution: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1">Specialty</label>
                  <input
                    type="text"
                    required
                    value={memberForm.specialty}
                    onChange={(e) => setMemberForm({ ...memberForm, specialty: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1">Photo Image URL</label>
                  <input
                    type="url"
                    value={memberForm.image_url}
                    onChange={(e) => setMemberForm({ ...memberForm, image_url: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm"
                  />
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-[#E2E8F0]">
                  <button
                    type="button"
                    onClick={() => setMemberModalOpen(false)}
                    className="px-4 py-2 text-xs font-semibold text-[#64748B]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-[#0D9488] hover:bg-[#0F766E] text-white text-xs font-bold rounded-sm shadow-sm"
                  >
                    Save Member
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
